import { isPlatformServer } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import * as AlertActions from '@curbnturf/alert/src/lib/+state/alert.actions';
import { AuthService } from '@curbnturf/auth/src/lib/auth.service';
import { BoondockService } from '@curbnturf/boondock-listing';
import * as BoondockActions from '@curbnturf/boondock-listing/src/lib/+state/boondock.actions';
import {
  AuthHelpers,
  Boondock,
  FlowStepType,
  FlowType,
  HOST_MULTISITE_FLOW,
  HOST_SINGLESITE_FLOW,
  IBoondock,
  IListing,
  ISite,
  IUser,
  Listing,
  LogLevel,
  NGRX_NO_ACTION,
  Role,
  SALES_MULTISITE_FLOW,
  SALES_SINGLESITE_FLOW,
  User,
} from '@curbnturf/entities';
import { ListingService } from '@curbnturf/listing';
import * as ListingActions from '@curbnturf/listing/src/lib/+state/listing.actions';
import * as LogActions from '@curbnturf/network/src/lib/log/+state/log.actions';
import * as NetworkActions from '@curbnturf/network/src/lib/network/+state/network.actions';
import * as NetworkSelectors from '@curbnturf/network/src/lib/network/+state/network.selectors';
import * as SiteActions from '@curbnturf/site/src/lib/+state/site.actions';
import { SiteService } from '@curbnturf/site/src/lib/site.service';
import { GlobalSpinnerService } from '@curbnturf/ui/src/lib/spinner/global-spinner.service';
import { GrowService, UserMarketingService, UserService } from '@curbnturf/user';
import * as UserActions from '@curbnturf/user/src/lib/+state/user.actions';
import { UserActionTypes } from '@curbnturf/user/src/lib/+state/user.actions';
import * as UserSelectors from '@curbnturf/user/src/lib/+state/user.selectors';
import { AlertController, NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { exhaustMap, forkJoin, from, of } from 'rxjs';
import { catchError, debounceTime, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import * as SignUpStateActions from './sign-up-state.actions';
import { SignUpStateActionTypes } from './sign-up-state.actions';
import * as SignUpStateSelectors from './sign-up-state.selectors';

const SPINNER_DEBOUNCE_TIME = 30000;

export const init$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(SignUpStateActions.init),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[action, user], currentUser]) => {
        if (
          currentUser &&
          // this condition needs the admin exception so that it doesn't skip this step.
          (currentUser.role === Role.sales ||
            (currentUser.role?.toLowerCase() as Role) === Role.salesManager ||
            currentUser.role === Role.admin)
        ) {
          return [ListingActions.init()];
        } else if (!user?.id) {
          return [ListingActions.init(), SignUpStateActions.restoreUser()];
        } else {
          return [ListingActions.init()];
        }
      }),
    ),
  { functional: true },
);

export const nextStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), router = inject(Router)) =>
    actions$.pipe(
      ofType(SignUpStateActions.nextStep),
      withLatestFrom(store.select(SignUpStateSelectors.nextStep)),
      mergeMap(([action, nextStep]) => {
        if (!nextStep || nextStep.route.length < 1) {
          throw new Error('Next Step Not Found');
        }

        return from(router.navigate(nextStep.route)).pipe(
          mergeMap(() => [SignUpStateActions.setCurrentStep({ step: nextStep }), SignUpStateActions.hideSpinner()]),
          catchError(() => [
            AlertActions.displayAlert({
              title: 'Navigation Error',
              body: 'Failed to find the next step in the sign up process. If this problem persists, please contact support.',
            }),
          ]),
        );
      }),
      catchError((err) => {
        return [
          LogActions.logMessage({
            log: {
              level: LogLevel.ERROR,
              message: 'Failed to navigate to the next step in the sign up process.',
              details: err,
            },
          }),
        ];
      }),
    ),
  { functional: true },
);

export const previousStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), router = inject(Router)) =>
    actions$.pipe(
      ofType(SignUpStateActions.previousStep),
      withLatestFrom(store.select(SignUpStateSelectors.previousStep)),
      mergeMap(([action, previousStep]) => {
        if (!previousStep || previousStep.route.length < 1) {
          throw new Error('Current Step Not Found');
        }

        return from(router.navigate(previousStep.route)).pipe(
          mergeMap(() => [SignUpStateActions.setCurrentStep({ step: previousStep }), SignUpStateActions.hideSpinner()]),
          catchError((err) => {
            return [
              AlertActions.displayAlert({
                title: 'Navigation Error',
                body: 'Failed to find the previous step in the sign up process. If this problem persists, please contact support.',
              }),
              LogActions.logMessage({
                log: {
                  level: LogLevel.ERROR,
                  message: 'Navigation Error',
                  details: err,
                },
              }),
            ];
          }),
        );
      }),
      catchError((err) => {
        return [
          LogActions.logMessage({
            log: {
              level: LogLevel.ERROR,
              message: 'Failed to navigate to the previous step in the sign up process.',
              details: err,
            },
          }),
        ];
      }),
    ),
  { functional: true },
);

export const setCurrentStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(SignUpStateActions.setCurrentStep),
      withLatestFrom(store.select(SignUpStateSelectors.getFlowType)),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      map(([[[action, flowType], listing], user]) => {
        if (
          // don't check if listing set if we are on the role, source, or phone step
          action.step?.type !== FlowStepType.RoleSelector &&
          action.step?.type !== FlowStepType.Source &&
          action.step?.type !== FlowStepType.ProfilePhone &&
          // only check if a listing is set if we are on the listing flow
          flowType === FlowType.listing &&
          !listing?.id &&
          !listing?.syncId
        ) {
          return SignUpStateActions.startNewListing({ user });
        }

        return NGRX_NO_ACTION;
      }),
    ),
  { functional: true },
);

// This extracts to actual step object from the step list.
export const setCurrentStepType$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(SignUpStateActions.setCurrentStepType),
      // debounce is to prevent the issue caused by defaulting to source and loading current process both setting current step
      debounceTime(0),
      withLatestFrom(store.select(SignUpStateSelectors.getSteps)),
      map(([action, steps]) => {
        const foundStep = steps.find((el) => el.type === action.step);
        if (foundStep) {
          return SignUpStateActions.setCurrentStep({ step: foundStep });
        }
        // Although maybe this should be an error instead
        return NGRX_NO_ACTION;
      }),
    ),
  { functional: true },
);

export const saveState$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    router = inject(Router),
    userService = inject(UserService),
    siteService = inject(SiteService),
  ) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveState),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[[action, listing], user], status]) => {
        if (status.connected) {
          if (listing && user) {
            return forkJoin([siteService.upsert(listing), userService.update(user)]).pipe(
              mergeMap(([savedListing, savedUser]) => {
                const actions: Action[] = [
                  SignUpStateActions.saveStateSuccess({ user: savedUser, listing: savedListing }),
                ];

                if (savedUser.inProgressId !== savedListing.id) {
                  actions.push(SignUpStateActions.updateInProgressListingId());
                }

                if (action.redirect?.route) {
                  return from(router.navigate(action.redirect.route)).pipe(
                    mergeMap(() => [...actions, SignUpStateActions.hideSpinner()]),
                  );
                } else {
                  return [...actions, SignUpStateActions.nextStep({})];
                }
              }),
            );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site or User data is missing.' })];
        } else {
          const actions: Action[] = [];
          if (listing && Object.keys(listing)?.length) {
            if (!listing?.syncId) {
              listing = { ...listing, syncId: uuidv4() };
            }
            actions.push(NetworkActions.pendingListingUpdate({ listing }));
          }
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
          }

          return [SignUpStateActions.saveStateSuccess({ user, listing }), SignUpStateActions.nextStep({}), ...actions];
        }
      }),
    ),
  { functional: true },
);

export const updateInProgressListingId$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.updateInProgressListingId),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[[action, user], listing], status]) => {
        if (status.connected) {
          if (user) {
            return userService.update(user).pipe(
              mergeMap((savedUser) => {
                return [
                  {
                    type: UserActionTypes.SetInProgressListing,
                    payload: { user, listing },
                  },
                  SignUpStateActions.saveStateSuccess({ user: savedUser }),
                ];
              }),
              catchError((error) => [SignUpStateActions.saveStateFailed({ error })]),
            );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site or User data is missing.' })];
        } else {
          // @todo Do something different because we are offline.
          return [NGRX_NO_ACTION];
        }
      }),
    ),
  { functional: true },
);

export const setSiteTypeHost$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    userService = inject(UserService),
    listingService = inject(ListingService),
  ) =>
    actions$.pipe(
      ofType(SignUpStateActions.setListingTypeHost),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[[action, listing], user], status], authUser]) => {
        if (status.connected) {
          if (listing && user) {
            return listingService.upsert(listing).pipe(
              mergeMap((savedListing: IListing) => {
                return userService.update({ ...user, inProgressId: savedListing.id }).pipe(
                  mergeMap((user: IUser) => {
                    const actions: Action[] = [SignUpStateActions.setListingTypeHostSuccess({ listing, user })];
                    if (AuthHelpers.isUserRole(authUser)) {
                      actions.push(UserActions.updateCurrentUserLocally({ user }));
                    }
                    return actions;
                  }),
                );
              }),
            );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site or User data is missing.' })];
        } else {
          const actions: Action[] = [SignUpStateActions.setListingTypeHostSuccess({ listing, user })];
          if (listing && Object.keys(listing)?.length) {
            if (!listing?.syncId) {
              listing = { ...listing, syncId: uuidv4() };
            }
            actions.push(NetworkActions.pendingListingUpdate({ listing }));
          }
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
            if (AuthHelpers.isUserRole(authUser)) {
              actions.push(UserActions.updateCurrentUserLocally({ user }));
            }
          }
          return actions;
        }
      }),
      catchError((err) => [SignUpStateActions.saveStateFailed({ error: err })]),
    ),
  { functional: true },
);

export const setBoondockPropertyTypeStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.setBoondockPropertyTypeStep),
      withLatestFrom(store.select(SignUpStateSelectors.getBoondock)),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[[action, boondock], user], status], authUser]) => {
        if (status.connected) {
          if (boondock && user) {
            return boondockService.upsert({ ...boondock, landTypes: action.landTypes }).pipe(
              mergeMap((savedBoondock: IBoondock) => {
                const actions: Action[] = [
                  SignUpStateActions.setBoondockStepSuccess({ boondock: savedBoondock, user }),
                ];
                return actions;
              }),
            );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Boondock Site or User data is missing.' })];
        } else {
          const actions: Action[] = [SignUpStateActions.setBoondockStepSuccess({ boondock, user })];
          if (boondock && Object.keys(boondock)?.length) {
            if (!boondock?.syncId) {
              boondock = { ...boondock, syncId: uuidv4() };
            }
            actions.push(NetworkActions.pendingBoondockUpdate({ boondock }));
          }
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
            if (AuthHelpers.isUserRole(authUser)) {
              actions.push(UserActions.updateCurrentUserLocally({ user }));
            }
          }
          return actions;
        }
      }),
      catchError((err) => [SignUpStateActions.saveStateFailed({ error: err })]),
    ),
  { functional: true },
);

export const setSiteTypeHostSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        SignUpStateActions.setListingTypeHostSuccess,
        SignUpStateActions.setBoondockStepSuccess,
        SignUpStateActions.startUserTentFlow,
        SignUpStateActions.startUserRvFlow,
      ),
      mergeMap((action) => [SignUpStateActions.nextStep({})]),
    ),
  { functional: true },
);

export const saveRoleSelectStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.startHostFlow, SignUpStateActions.startGuestFlow),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[action, user], status], authUser]) => {
        if (status.connected) {
          if (user) {
            return userService.update(user).pipe(
              mergeMap((savedUser) => {
                const actions: Action[] = [
                  SignUpStateActions.saveStateSuccess({ user: savedUser }),
                  SignUpStateActions.nextStep({}),
                ];

                if (action.type === SignUpStateActionTypes.SignUpStartHostFlow) {
                  if (savedUser.inProgressId) {
                    actions.push(SignUpStateActions.setInProgressListing({ listing: { id: savedUser.inProgressId } }));
                  } else {
                    actions.push(SignUpStateActions.startNewListingAndFlow({ user: savedUser }));
                  }
                }

                if (AuthHelpers.isUserRole(authUser)) {
                  actions.push(UserActions.updateCurrentUserLocally({ user }));
                }
                return actions;
              }),
            );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site or User data is missing.' })];
        } else {
          const actions: Action[] = [SignUpStateActions.saveStateSuccess({ user }), SignUpStateActions.nextStep({})];
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
            if (AuthHelpers.isUserRole(authUser)) {
              actions.push(UserActions.updateCurrentUserLocally({ user }));
            }
          }
          return actions;
        }
      }),
    ),
  { functional: true },
);

export const savePhoneStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.savePhoneStep),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[action, user], status], authUser]) => {
        if (status.connected) {
          if (user) {
            return userService.update(user).pipe(
              mergeMap((savedUser) => {
                return userService.postLead(savedUser, action.assistance).pipe(
                  mergeMap(() => {
                    const actions: Action[] = [
                      SignUpStateActions.saveUserState({ user: savedUser }),
                      SignUpStateActions.nextStep({}),
                    ];
                    if (AuthHelpers.isUserRole(authUser)) {
                      actions.push(UserActions.updateCurrentUserLocally({ user }));
                    }
                    return actions;
                  }),
                  catchError((err) => [SignUpStateActions.saveStateFailed({ error: err })]),
                );
              }),
            );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'User data is missing.' })];
        } else {
          const actions: Action[] = [SignUpStateActions.nextStep({})];
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
            if (AuthHelpers.isUserRole(authUser)) {
              actions.push(UserActions.updateCurrentUserLocally({ user }));
            }
          }
          return actions;
        }
      }),
      catchError((err) => [SignUpStateActions.saveStateFailed({ error: err })]),
    ),
  { functional: true },
);

export const saveUserStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveUserRvSizeStep, SignUpStateActions.saveUserRoadConditionStep),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[action, user], status], authUser]) => {
        if (status.connected) {
          if (user) {
            return userService.update(user).pipe(
              mergeMap((savedUser) => {
                const actions: Action[] = [
                  SignUpStateActions.saveStateSuccess({ user: savedUser }),
                  SignUpStateActions.nextStep({}),
                ];
                if (AuthHelpers.isUserRole(authUser)) {
                  actions.push(UserActions.updateCurrentUserLocally({ user: savedUser }));
                }
                return actions;
              }),
            );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'User data is missing.' })];
        } else {
          const actions: Action[] = [SignUpStateActions.saveStateSuccess({ user }), SignUpStateActions.nextStep({})];
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
            if (AuthHelpers.isUserRole(authUser)) {
              actions.push(UserActions.updateCurrentUserLocally({ user }));
            }
          }
          return actions;
        }
      }),
    ),
  { functional: true },
);

export const saveSourceStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveSourceStep),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[action, user], status], authUser]) => {
        if (status.connected) {
          if (user) {
            return userService.update(user).pipe(
              mergeMap((savedUser) => {
                const actions: Action[] = [
                  SignUpStateActions.saveUserState({ user: savedUser }),
                  SignUpStateActions.nextStep({}),
                ];
                if (AuthHelpers.isUserRole(authUser)) {
                  actions.push(UserActions.updateCurrentUserLocally({ user: savedUser }));
                }
                return actions;
              }),
            );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'User data is missing.' })];
        } else {
          const actions: Action[] = [SignUpStateActions.nextStep({})];
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
            if (AuthHelpers.isUserRole(authUser)) {
              actions.push(UserActions.updateCurrentUserLocally({ user }));
            }
          }
          return actions;
        }
      }),
    ),
  { functional: true },
);

export const saveListingLocationStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveLocationStep),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, listing], status]) => {
        if (status.connected) {
          if (listing && listing.id) {
            return listingService
              .updateLocation(listing.id, action)
              .pipe(
                mergeMap((savedListing: IListing) => [
                  SignUpStateActions.saveStateSuccess({ listing: savedListing }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (listing && Object.keys(listing)?.length) {
            if (!listing?.syncId) {
              listing = { ...listing, syncId: uuidv4() };
            }
            const actions: Action[] = [
              SignUpStateActions.saveStateSuccess({ listing }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const saveBoondockLocationStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveBoondockLocationStep),
      withLatestFrom(store.select(SignUpStateSelectors.getBoondock)),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[[action, boondock], user], status], authUser]) => {
        if (status.connected) {
          if (boondock && user) {
            return boondockService
              .upsert({
                ...boondock,
                address: action.address,
                coordinates: action.coordinates,
                landOwner: action.landOwner,
              })
              .pipe(
                mergeMap((savedBoondock: IBoondock) => {
                  const actions: Action[] = [
                    SignUpStateActions.setBoondockStepSuccess({ boondock: savedBoondock, user }),
                  ];
                  return actions;
                }),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Boondock Site or User data is missing.' })];
        } else {
          const actions: Action[] = [SignUpStateActions.setBoondockStepSuccess({ boondock, user })];
          if (boondock && Object.keys(boondock)?.length) {
            if (!boondock?.syncId) {
              boondock = { ...boondock, syncId: uuidv4() };
            }
            actions.push(NetworkActions.pendingBoondockUpdate({ boondock }));
          }
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
            if (AuthHelpers.isUserRole(authUser)) {
              actions.push(UserActions.updateCurrentUserLocally({ user }));
            }
          }
          return actions;
        }
      }),
    ),
  { functional: true },
);

export const saveListingBookingStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveBookingStep),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, listing], status]) => {
        if (status.connected) {
          if (listing && listing.id) {
            return listingService
              .updateBooking(listing.id, action)
              .pipe(
                mergeMap((savedListing: IListing) => [
                  SignUpStateActions.saveStateSuccess({ listing: savedListing }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (listing && Object.keys(listing)?.length) {
            // @todo Refactor offline features
            if (!listing?.syncId) {
              listing = { ...listing, syncId: uuidv4() };
            }
            listing = { ...listing, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveStateSuccess({ listing }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const saveListingMultiSiteStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveMultiSiteStep),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(SignUpStateSelectors.getSelectedSite)),
      withLatestFrom(store.select(SignUpStateSelectors.getFlowType)),
      mergeMap(([[[[action, listing], status], selectedSite], flowType]) => {
        if (status.connected) {
          if (listing && listing.id) {
            if (listing.sites && Array.isArray(listing.sites) && listing.sites.length >= 1) {
              const actions = [];
              if (!selectedSite) {
                // @ts-ignore - this will always return a valid site.
                const selectedSite: ISite = listing.sites.slice().pop();
                actions.push(SignUpStateActions.selectSiteFromListing({ site: selectedSite }));
              }
              return [...actions, SignUpStateActions.saveStateSuccess({ listing }), SignUpStateActions.nextStep({})];
            } else {
              const numberOfSites = action.multisite !== 'single' ? 2 : 1;
              return listingService.addNewSites(listing.id, numberOfSites).pipe(
                mergeMap((savedListing: IListing) => {
                  const actions: Action[] = [
                    SignUpStateActions.saveStateSuccess({ listing: savedListing }),
                    SignUpStateActions.nextStep({}),
                  ];
                  if (savedListing.sites && savedListing.sites.length > 1) {
                    if (flowType === FlowType.sales) {
                      actions.push(SignUpStateActions.switchWizardFlow({ flow: SALES_MULTISITE_FLOW.slice() }));
                    } else {
                      actions.push(SignUpStateActions.switchWizardFlow({ flow: HOST_MULTISITE_FLOW.slice() }));
                    }
                  } else {
                    if (flowType === FlowType.sales) {
                      actions.push(SignUpStateActions.switchWizardFlow({ flow: SALES_SINGLESITE_FLOW.slice() }));
                    } else {
                      actions.push(SignUpStateActions.switchWizardFlow({ flow: HOST_SINGLESITE_FLOW.slice() }));
                    }
                  }
                  // @ts-ignore - this will always return a valid site.
                  const selectedSite: ISite = savedListing.sites.slice().pop();
                  actions.push(SignUpStateActions.selectSiteFromListing({ site: selectedSite }));
                  return actions;
                }),
              );
            }
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (listing && Object.keys(listing)?.length) {
            // @todo Refactor offline features
            if (!listing?.syncId) {
              listing = { ...listing, syncId: uuidv4() };
            }
            listing = { ...listing, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveStateSuccess({ listing }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const saveSiteDetailStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), siteService = inject(SiteService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveDetailsStep),
      withLatestFrom(store.select(SignUpStateSelectors.getSelectedSite)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, site], status]) => {
        if (status.connected) {
          if (site && site.id) {
            return siteService
              .updateDetails(site.id, action)
              .pipe(
                mergeMap((savedSite: ISite) => [
                  SignUpStateActions.saveSiteStateSuccess({ site: savedSite }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (site) {
            // @todo Refactor offline features
            if (!site?.syncId) {
              site = { ...site, syncId: uuidv4() };
            }
            site = { ...site, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveSiteStateSuccess({ site }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingSiteUpdate({ site }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const savePropertyTypeStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.savePropertyTypeStep),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, listing], status]) => {
        if (status.connected) {
          if (listing && listing.id && Array.isArray(action.types)) {
            return listingService
              .updatePropertyType(listing.id, action.types)
              .pipe(
                mergeMap((savedSite: ISite) => [
                  SignUpStateActions.saveSiteStateSuccess({ site: savedSite }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (listing && Object.keys(listing)?.length) {
            // @todo Refactor offline features
            if (!listing?.syncId) {
              listing = { ...listing, syncId: uuidv4() };
            }
            listing = { ...listing, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveStateSuccess({ listing }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const saveSiteAmenitiesStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveAmenitiesStep),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(SignUpStateSelectors.getSelectedSite)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[[action, listing], site], status]) => {
        if (status.connected) {
          if (listing && listing.id && site && site.id) {
            return listingService
              .updateAmenities(listing.id, site.id, action)
              .pipe(
                mergeMap((savedListing: IListing) => [
                  SignUpStateActions.saveStateSuccess({ listing: savedListing }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (listing && Object.keys(listing)?.length) {
            // @todo Refactor offline features
            if (!listing?.syncId) {
              listing = { ...listing, syncId: uuidv4() };
            }
            listing = { ...listing, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveStateSuccess({ listing }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const saveBoondockAmenitiesStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveBoondockAmenitiesStep),
      withLatestFrom(store.select(SignUpStateSelectors.getBoondock)),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[[action, boondock], user], status], authUser]) => {
        if (status.connected) {
          if (boondock && user) {
            return boondockService
              .upsert({ ...boondock, amenities: action.amenities, signalReception: action.signalReception })
              .pipe(
                mergeMap((savedBoondock: IBoondock) => {
                  const actions: Action[] = [
                    SignUpStateActions.setBoondockStepSuccess({ boondock: savedBoondock, user }),
                  ];
                  return actions;
                }),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Boondock Site or User data is missing.' })];
        } else {
          const actions: Action[] = [SignUpStateActions.setBoondockStepSuccess({ boondock, user })];
          if (boondock && Object.keys(boondock)?.length) {
            if (!boondock?.syncId) {
              boondock = { ...boondock, syncId: uuidv4() };
            }
            actions.push(NetworkActions.pendingBoondockUpdate({ boondock }));
          }
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
            if (AuthHelpers.isUserRole(authUser)) {
              actions.push(UserActions.updateCurrentUserLocally({ user }));
            }
          }
          return actions;
        }
      }),
    ),
  { functional: true },
);

export const saveActivitiesStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), siteService = inject(SiteService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveActivitiesStep),
      withLatestFrom(store.select(SignUpStateSelectors.getSelectedSite)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, site], status]) => {
        if (status.connected) {
          if (site && site.id && Array.isArray(action.activities)) {
            return siteService
              .updateActivities(site.id, action.activities)
              .pipe(
                mergeMap((savedSite: ISite) => [
                  SignUpStateActions.saveSiteStateSuccess({ site: savedSite }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (site) {
            // @todo Refactor offline features
            if (!site?.syncId) {
              site = { ...site, syncId: uuidv4() };
            }
            site = { ...site, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveSiteStateSuccess({ site }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingSiteUpdate({ site }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const saveBoondockActivitiesStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveBoondockActivitiesStep),
      withLatestFrom(store.select(SignUpStateSelectors.getBoondock)),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[[action, boondock], user], status], authUser]) => {
        if (status.connected) {
          if (boondock?.id && Array.isArray(action.activities)) {
            return boondockService.upsert({ ...boondock, activities: action.activities }).pipe(
              mergeMap((savedBoondock: IBoondock) => {
                const actions: Action[] = [
                  SignUpStateActions.setBoondockStepSuccess({ boondock: savedBoondock, user }),
                ];
                return actions;
              }),
            );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Boondock data is missing.' })];
        } else {
          const actions: Action[] = [SignUpStateActions.setBoondockStepSuccess({ boondock, user })];
          if (boondock && Object.keys(boondock)?.length) {
            if (!boondock?.syncId) {
              boondock = { ...boondock, syncId: uuidv4() };
            }
            actions.push(NetworkActions.pendingBoondockUpdate({ boondock }));
          }
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
            if (AuthHelpers.isUserRole(authUser)) {
              actions.push(UserActions.updateCurrentUserLocally({ user }));
            }
          }
          return actions;
        }
      }),
    ),
  { functional: true },
);

export const saveIsolationStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), siteService = inject(SiteService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveIsolationStep),
      withLatestFrom(store.select(SignUpStateSelectors.getSelectedSite)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, site], status]) => {
        if (status.connected) {
          if (site && site.id && Array.isArray(action.accessRoutes)) {
            return siteService
              .updateIsolation(site.id, action)
              .pipe(
                mergeMap((savedSite: ISite) => [
                  SignUpStateActions.saveSiteStateSuccess({ site: savedSite }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (site) {
            // @todo Refactor offline features
            if (!site?.syncId) {
              site = { ...site, syncId: uuidv4() };
            }
            site = { ...site, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveSiteStateSuccess({ site }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingSiteUpdate({ site }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const saveBoondockIsolationStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveBoondockIsolationStep),
      withLatestFrom(store.select(SignUpStateSelectors.getBoondock)),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[[action, boondock], user], status], authUser]) => {
        if (status.connected) {
          if (boondock && user) {
            return boondockService
              .upsert({
                ...boondock,
                noise: action.noise,
                privacy: action.privacy,
                accessRoutes: action.accessRoutes,
                potentialHazards: action.potentialHazards,
                potentialHazardDescription: action.potentialHazardDescription,
              })
              .pipe(
                mergeMap((savedBoondock: IBoondock) => {
                  const actions: Action[] = [
                    SignUpStateActions.setBoondockStepSuccess({ boondock: savedBoondock, user }),
                  ];
                  return actions;
                }),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Boondock data is missing.' })];
        } else {
          const actions: Action[] = [SignUpStateActions.setBoondockStepSuccess({ boondock, user })];
          if (boondock && Object.keys(boondock)?.length) {
            if (!boondock?.syncId) {
              boondock = { ...boondock, syncId: uuidv4() };
            }
            actions.push(NetworkActions.pendingBoondockUpdate({ boondock }));
          }
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
            if (AuthHelpers.isUserRole(authUser)) {
              actions.push(UserActions.updateCurrentUserLocally({ user }));
            }
          }
          return actions;
        }
      }),
    ),
  { functional: true },
);

export const saveBoondockLandStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveBoondockLandStep),
      withLatestFrom(store.select(SignUpStateSelectors.getBoondock)),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[[action, boondock], user], status], authUser]) => {
        if (status.connected) {
          if (boondock && user) {
            return boondockService
              .upsert({
                ...boondock,
                amenities: [...(boondock?.amenities || []), ...(action.boondock?.amenities || [])],
                sizeCars: action.boondock?.sizeCars,
                sizeBuses: action.boondock?.sizeBuses,
              })
              .pipe(
                mergeMap((savedBoondock: IBoondock) => {
                  const actions: Action[] = [
                    SignUpStateActions.setBoondockStepSuccess({ boondock: savedBoondock, user }),
                  ];
                  return actions;
                }),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Boondock data is missing.' })];
        } else {
          const actions: Action[] = [SignUpStateActions.setBoondockStepSuccess({ boondock, user })];
          if (boondock && Object.keys(boondock)?.length) {
            if (!boondock?.syncId) {
              boondock = { ...boondock, syncId: uuidv4() };
            }
            actions.push(NetworkActions.pendingBoondockUpdate({ boondock }));
          }
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
            if (AuthHelpers.isUserRole(authUser)) {
              actions.push(UserActions.updateCurrentUserLocally({ user }));
            }
          }
          return actions;
        }
      }),
    ),
  { functional: true },
);

export const saveVehiclesStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), siteService = inject(SiteService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveVehiclesStep),
      withLatestFrom(store.select(SignUpStateSelectors.getSelectedSite)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, site], status]) => {
        if (status.connected) {
          if (site && site.id) {
            return siteService
              .updateVehicles(site.id, action)
              .pipe(
                mergeMap((savedSite: ISite) => [
                  SignUpStateActions.saveSiteStateSuccess({ site: savedSite }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (site) {
            // @todo Refactor offline features
            if (!site?.syncId) {
              site = { ...site, syncId: uuidv4() };
            }
            site = { ...site, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveSiteStateSuccess({ site }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingSiteUpdate({ site }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const savePriceStep$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    listingService = inject(ListingService),
    siteService = inject(SiteService),
  ) =>
    actions$.pipe(
      ofType(SignUpStateActions.savePriceStep),
      withLatestFrom(store.select(SignUpStateSelectors.getSelectedSite)),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[[action, site], listing], status]) => {
        if (status.connected) {
          if (site?.id && listing?.id && action.price && action.cancellationPolicy) {
            return forkJoin({
              requestListing: listingService.updateBooking(listing.id, {
                cancellationPolicy: action.cancellationPolicy,
              }),
              requestSite: siteService.updatePrice(site.id, { price: action.price }),
            }).pipe(
              mergeMap(({ requestListing, requestSite }) => [
                SignUpStateActions.saveStateSuccess({ listing: requestListing }),
                SignUpStateActions.saveSiteStateSuccess({ site: requestSite }),
                SignUpStateActions.nextStep({}),
              ]),
            );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (site) {
            // @todo Refactor offline features
            if (!site?.syncId) {
              site = { ...site, syncId: uuidv4() };
            }
            site = { ...site, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveSiteStateSuccess({ site }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingSiteUpdate({ site }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const saveSiteNameStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), siteService = inject(SiteService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveSiteNameStep),
      withLatestFrom(store.select(SignUpStateSelectors.getSelectedSite)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, site], status]) => {
        if (status.connected) {
          if (site && site.id && action.name) {
            return siteService
              .updateName(site.id, action)
              .pipe(
                mergeMap((savedSite: ISite) => [
                  SignUpStateActions.saveSiteStateSuccess({ site: savedSite }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (site) {
            // @todo Refactor offline features
            if (!site?.syncId) {
              site = { ...site, syncId: uuidv4() };
            }
            site = { ...site, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveSiteStateSuccess({ site }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingSiteUpdate({ site }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const saveDescriptionStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveDescriptionStep),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, listing], status]) => {
        if (status.connected) {
          if (listing && listing.id && action.name) {
            return listingService
              .updateDescription(listing.id, action)
              .pipe(
                mergeMap((savedListing: IListing) => [
                  SignUpStateActions.saveStateSuccess({ listing: savedListing }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (listing && Object.keys(listing)?.length) {
            // @todo Refactor offline features
            if (!listing?.syncId) {
              listing = { ...listing, syncId: uuidv4() };
            }
            listing = { ...listing, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveStateSuccess({ listing }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const saveBoondockDescriptionStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveBoondockDescriptionStep),
      withLatestFrom(store.select(SignUpStateSelectors.getBoondock)),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[[action, boondock], user], status], authUser]) => {
        if (status.connected) {
          if (boondock && user) {
            return boondockService
              .upsert({
                ...boondock,
                name: action.name,
                description: action.description,
              })
              .pipe(
                mergeMap((savedBoondock: IBoondock) => {
                  const actions: Action[] = [
                    SignUpStateActions.setBoondockStepSuccess({ boondock: savedBoondock, user }),
                  ];
                  return actions;
                }),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Boondock data is missing.' })];
        } else {
          const actions: Action[] = [SignUpStateActions.setBoondockStepSuccess({ boondock, user })];
          if (boondock && Object.keys(boondock)?.length) {
            if (!boondock?.syncId) {
              boondock = { ...boondock, syncId: uuidv4() };
            }
            actions.push(NetworkActions.pendingBoondockUpdate({ boondock }));
          }
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
            if (AuthHelpers.isUserRole(authUser)) {
              actions.push(UserActions.updateCurrentUserLocally({ user }));
            }
          }
          return actions;
        }
      }),
    ),
  { functional: true },
);

export const saveSitePhotosStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), siteService = inject(SiteService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveSitePhotosStep),
      withLatestFrom(store.select(SignUpStateSelectors.getSelectedSite)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, site], status]) => {
        if (status.connected) {
          if (site && site.id && Array.isArray(action.photos)) {
            return siteService
              .updateSitePhotos(site.id, action.photos)
              .pipe(
                mergeMap((savedSite: ISite) => [
                  SignUpStateActions.saveSiteStateSuccess({ site: savedSite }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (site) {
            // @todo Refactor offline features
            if (!site?.syncId) {
              site = { ...site, syncId: uuidv4() };
            }
            site = { ...site, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveSiteStateSuccess({ site }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingSiteUpdate({ site }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const savePhotosStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.savePhotosStep),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, listing], status]) => {
        if (status.connected) {
          if (listing && listing.id && Array.isArray(action.photos)) {
            return listingService
              .updatePhotos(listing.id, action.photos)
              .pipe(
                mergeMap((savedListing: IListing) => [
                  SignUpStateActions.saveStateSuccess({ listing: savedListing }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (listing && Object.keys(listing)?.length) {
            // @todo Refactor offline features
            if (!listing?.syncId) {
              listing = { ...listing, syncId: uuidv4() };
            }
            listing = { ...listing, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveStateSuccess({ listing }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const saveBoondockPhotosStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveBoondockPhotosStep),
      withLatestFrom(store.select(SignUpStateSelectors.getBoondock)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, boondock], status]) => {
        if (status.connected) {
          if (boondock?.id && Array.isArray(action.photos)) {
            return boondockService
              .updatePhotos(boondock.id, action.photos)
              .pipe(
                mergeMap((savedBoondock: IBoondock) => [
                  SignUpStateActions.saveStateSuccess({ boondock: savedBoondock }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Boondock data is missing.' })];
        } else {
          if (boondock && Object.keys(boondock)?.length) {
            // @todo Refactor offline features
            if (!boondock?.syncId) {
              boondock = { ...boondock, syncId: uuidv4() };
            }
            boondock = { ...boondock, ...action };
            const actions: Action[] = [
              SignUpStateActions.saveStateSuccess({ boondock }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingBoondockUpdate({ boondock }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Boondock data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const savePoliciesStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.savePoliciesStep),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, listing], status]) => {
        if (status.connected) {
          if (listing && listing.id && action.policies) {
            return listingService
              .updatePolicies(listing.id, action.policies)
              .pipe(
                mergeMap((savedListing: IListing) => [
                  SignUpStateActions.saveStateSuccess({ listing: savedListing }),
                  SignUpStateActions.nextStep({}),
                ]),
              );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Listing data is missing.' })];
        } else {
          if (listing && Object.keys(listing)?.length) {
            if (!listing?.syncId) {
              listing = { ...listing, syncId: uuidv4() };
            }
            const actions: Action[] = [
              SignUpStateActions.saveStateSuccess({ listing }),
              SignUpStateActions.nextStep({}),
              NetworkActions.pendingListingUpdate({ listing }),
            ];
            return actions;
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const updateSitePhotoDetails$ = createEffect(
  (actions$ = inject(Actions), siteService = inject(SiteService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.updateSitePhotoDetails),
      mergeMap((action) => {
        return siteService.updateSitePhotos(action.siteId, action.photos).pipe(
          mergeMap((savedSite: ISite) => {
            const actions: Action[] = [SignUpStateActions.saveSiteStateSuccess({ site: savedSite })];
            if (action.continueWizard) {
              actions.push(SignUpStateActions.nextStep({}));
            }
            return actions;
          }),
          catchError((err) => [
            SignUpStateActions.saveStateFailed({
              error: err?.error?.message || err?.message || err || 'Error saving site photos and captions.',
            }),
          ]),
        );
      }),
      catchError((err) => [
        SignUpStateActions.saveStateFailed({
          error: err?.error?.message || err?.message || err || 'Error saving site photos and captions.',
        }),
      ]),
    ),
  { functional: true },
);

export const updateListingPhotoDetails$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.updateListingPhotoDetails),
      mergeMap((action) => {
        return listingService.updatePhotos(action.listingId, action.photos).pipe(
          mergeMap((savedListing: IListing) => {
            const actions: Action[] = [SignUpStateActions.saveStateSuccess({ listing: savedListing })];
            if (action.continueWizard) {
              actions.push(SignUpStateActions.nextStep({}));
            }
            return actions;
          }),
          catchError((err) => [
            SignUpStateActions.saveStateFailed({
              error: err?.error?.message || err?.message || err || 'Error saving site photos and captions.',
            }),
          ]),
        );
      }),
      catchError((err) => [
        SignUpStateActions.saveStateFailed({
          error: err?.error?.message || err?.message || err || 'Error saving site photos and captions.',
        }),
      ]),
    ),
  { functional: true },
);

export const updateBoondockPhotoDetails$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.updateBoondockPhotoDetails),
      mergeMap((action) => {
        return boondockService.updatePhotos(action.boondockId, action.photos).pipe(
          mergeMap((savedBoondock: IBoondock) => {
            const actions: Action[] = [SignUpStateActions.saveStateSuccess({ boondock: savedBoondock })];
            if (action.continueWizard) {
              actions.push(SignUpStateActions.nextStep({}));
            }
            return actions;
          }),
          catchError((err) => [
            SignUpStateActions.saveStateFailed({
              error: err?.error?.message || err?.message || err || 'Error saving boondock photos and captions.',
            }),
          ]),
        );
      }),
      catchError((err) => [
        SignUpStateActions.saveStateFailed({
          error: err?.error?.message || err?.message || err || 'Error saving boondock photos and captions.',
        }),
      ]),
    ),
  { functional: true },
);

export const duplicateSite$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), siteService = inject(SiteService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.duplicateSite),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected) {
          if (action.site && action.site.id && action.name) {
            return siteService.duplicateSite(action.site.id, action.name).pipe(
              mergeMap((createdSite: ISite) => [SignUpStateActions.duplicateSiteSuccess({ site: createdSite })]),
              catchError((err) => [SignUpStateActions.duplicateSiteError({ err })]),
            );
          }
          return [SignUpStateActions.duplicateSiteError({ err: 'Site data is missing.' })];
        } else {
          if (action.site && action.site.id && action.name) {
            // @todo Refactor offline features
            return [
              SignUpStateActions.duplicateSiteSuccess({
                site: {
                  /* @todo Complete the new site */
                },
              }),
            ];
          }
          return [SignUpStateActions.saveStateFailed({ error: 'Site data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const addNewSite$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.addNewSite),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, listing], status]) => {
        if (status.connected) {
          if (listing && listing.id) {
            return listingService.addNewSites(listing.id, 1).pipe(
              mergeMap((updatedListing: IListing) => [
                SignUpStateActions.addNewSiteSuccess({ listing: updatedListing }),
              ]),
              catchError((err) => [SignUpStateActions.addNewSiteError({ err })]),
            );
          }
          return [SignUpStateActions.addNewSiteError({ err: 'Listing data is missing.' })];
        } else {
          if (listing && listing.id) {
            // @todo Refactor offline features
            return [
              SignUpStateActions.addNewSiteSuccess({
                listing: {
                  /* @todo Complete the new site */
                },
              }),
            ];
          }
          return [SignUpStateActions.addNewSiteError({ err: 'Listing data is missing.' })];
        }
      }),
    ),
  { functional: true },
);

export const sendOwnerEmail$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), growService = inject(GrowService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.sendOwnerEmail),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      mergeMap(([action, user]) => {
        if (user?.id) {
          return growService.sendWelcomeEmail(user.id).pipe(
            mergeMap((sent: boolean) => [SignUpStateActions.sendOwnerEmailSuccess({ user })]),
            catchError((err) => [SignUpStateActions.sendOwnerEmailFailed({ err })]),
          );
        }
        return [SignUpStateActions.sendOwnerEmailFailed({ err: 'User data is missing.' })];
      }),
    ),
  { functional: true },
);

export const addNewSiteSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        SignUpStateActions.duplicateSiteSuccess,
        SignUpStateActions.addNewSiteSuccess,
        SignUpStateActions.addNewSiteError,
      ),
      mergeMap(() => {
        return [SignUpStateActions.hideSpinner()];
      }),
    ),
  { functional: true },
);

export const editSelectedSite$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SignUpStateActions.editSelectedSite),
      mergeMap((action) => {
        return [
          SignUpStateActions.selectSiteFromListing({ site: action.selectedSite }),
          SignUpStateActions.goToStep({ step: FlowStepType.SiteDetails }),
        ];
      }),
    ),
  { functional: true },
);

export const goToStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), navCtrl = inject(NavController)) =>
    actions$.pipe(
      ofType(SignUpStateActions.goToStep),
      withLatestFrom(store.select(SignUpStateSelectors.getSteps)),
      mergeMap(([action, steps]) => {
        const found = steps.find((el) => el.type === action.step);

        if (found) {
          return of(navCtrl.navigateForward(found.route)).pipe(exhaustMap(() => [NGRX_NO_ACTION]));
        } else {
          return [NGRX_NO_ACTION];
        }
      }),
    ),
  { functional: true },
);

export const enableSite$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), siteService = inject(SiteService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.enableSite),
      mergeMap((action) =>
        siteService.enable(action.site).pipe(
          mergeMap((enableResult) => {
            if (enableResult.success) {
              return [SignUpStateActions.enableSiteSuccess({ site: action.site })];
            } else {
              return [
                AlertActions.displayAlert({
                  title: 'Error Enabling Site',
                  body: 'An error occurred while enabling the site: ' + enableResult.message,
                  level: 'error',
                }),
              ];
            }
          }),
          catchError((err) => [
            LogActions.logMessage({
              log: {
                level: LogLevel.ERROR,
                message: 'An error occurred while trying to enable the site.',
                details: err,
                subjectId: action.site.id,
                subjectType: 'site',
              },
            }),
            AlertActions.displayAlert({
              title: 'Error Enabling Site',
              body: 'An error occurred while enabling the site: ' + err,
              level: 'error',
            }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const disableSite$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), siteService = inject(SiteService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.disableSite),
      mergeMap((action) =>
        siteService.disable(action.site).pipe(
          mergeMap((disableResult) => {
            if (disableResult.success) {
              return [SignUpStateActions.disableSiteSuccess({ site: action.site })];
            } else {
              return [
                AlertActions.displayAlert({
                  title: 'Error Disabling Site',
                  body: 'An error occurred while enabling the site: ' + disableResult.message,
                  level: 'error',
                }),
              ];
            }
          }),
          catchError((err) => [
            LogActions.logMessage({
              log: {
                level: LogLevel.ERROR,
                message: 'An error occurred while trying to disable the site.',
                details: err,
                subjectId: action.site.id,
                subjectType: 'site',
              },
            }),
            AlertActions.displayAlert({
              title: 'Error Disabling Site',
              body: 'An error occurred while enabling the site: ' + err,
              level: 'error',
            }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const removeSite = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SiteActions.siteRemoved),
      mergeMap((action) => [SignUpStateActions.removeSite({ site: action.site })]),
    ),
  { functional: true },
);

export const saveUserProfilePhotoStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(SignUpStateActions.updateProfilePhoto),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[action, user], authUser]) => {
        if (user) {
          if (AuthHelpers.isUserRole(authUser)) {
            return [UserActions.updateCurrentUserLocally({ user })];
          }
          return [NGRX_NO_ACTION];
        }
        return [SignUpStateActions.saveStateFailed({ error: 'User data is missing.' })];
      }),
    ),
  { functional: true },
);

export const completeSalesPublishStep$ = createEffect(
  (actions$ = inject(Actions), growService = inject(GrowService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.listingSalesComplete),
      mergeMap((action) => {
        return growService.notifyClientListingReady(action.listingId).pipe(
          map(() =>
            AlertActions.displayAlert({
              title: 'Client Notified',
              body: 'Your client has been notified and now needs to publish the listing. You should follow up with the client if they have not published their listing shortly',
              level: 'warning',
            }),
          ),
          catchError((err) => {
            return [
              LogActions.logMessage({
                log: {
                  level: LogLevel.ERROR,
                  message: 'Error when notifying user of completed listing.',
                  details: err,
                  subjectType: 'site',
                  subjectId: action.listingId,
                },
              }),
              AlertActions.displayAlert({
                title: 'Error Notifying Client',
                body: 'We attempted to notify your client, but an error has occurred. You may need to follow up with the client.',
                level: 'error',
              }),
            ];
          }),
        );
      }),
    ),
  { functional: true },
);

export const siteImagesUpdated$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SiteActions.siteImagesUpdated),
      mergeMap((action) => {
        if (action.id) {
          return [SignUpStateActions.updateSitePhotoDetails({ photos: action.photos, siteId: action.id })];
        } else {
          return [NGRX_NO_ACTION];
        }
      }),
    ),
  { functional: true },
);

export const updateListingPhotos$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.updateListingPhotos),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, listing], status]) => {
        if (status.connected) {
          if ((listing && !action.listingId) || (listing && action.listingId && action.listingId === listing.id)) {
            return listingService.update(listing).pipe(map(() => NGRX_NO_ACTION));
          }
          return [NGRX_NO_ACTION];
        } else {
          const actions: Action[] = [NGRX_NO_ACTION];
          if (listing && Object.keys(listing)?.length) {
            if (!listing?.syncId) {
              listing = { ...listing, syncId: uuidv4() };
            }
            actions.push(NetworkActions.pendingListingUpdate({ listing }));
          }
          return actions;
        }
      }),
    ),
  { functional: true },
);

export const updateBoondockPhotos$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.updateBoondockPhotos),
      withLatestFrom(store.select(SignUpStateSelectors.getBoondock)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[action, boondock], status]) => {
        if (status.connected) {
          if (
            (boondock && !action.boondockId) ||
            (boondock && action.boondockId && action.boondockId === boondock.id)
          ) {
            return boondockService.update(boondock).pipe(map(() => NGRX_NO_ACTION));
          }
          return [NGRX_NO_ACTION];
        } else {
          const actions: Action[] = [NGRX_NO_ACTION];
          if (boondock && Object.keys(boondock)?.length) {
            if (!boondock?.syncId) {
              boondock = { ...boondock, syncId: uuidv4() };
            }
            actions.push(NetworkActions.pendingBoondockUpdate({ boondock }));
          }
          return actions;
        }
      }),
    ),
  { functional: true },
);

export const saveUserRvStep$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveUserRvStep, SignUpStateActions.saveUserTentStep),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[action, user], status], authUser]) => {
        if (user) {
          if (status.connected) {
            return userService.update(user).pipe(
              mergeMap((savedUser) => {
                if (savedUser.rvs && savedUser.rvs.length > 0 && !savedUser.selectedRv) {
                  const selectedRv = savedUser.rvs[0];
                  const selectRvUser = { ...savedUser, selectedRv: { ...selectedRv } };
                  // Second user update to set the default RV
                  return userService.update(selectRvUser).pipe(
                    mergeMap((secondSavedUser) => {
                      const actions: Action[] = [
                        SignUpStateActions.saveStateSuccess({ user: secondSavedUser }),
                        SignUpStateActions.nextStep({}),
                      ];
                      if (AuthHelpers.isUserRole(authUser)) {
                        actions.push(UserActions.updateCurrentUserLocally({ user: secondSavedUser }));
                      }
                      return actions;
                    }),
                  );
                } else {
                  const actions: Action[] = [
                    SignUpStateActions.saveStateSuccess({ user: savedUser }),
                    SignUpStateActions.nextStep({}),
                  ];
                  if (AuthHelpers.isUserRole(authUser)) {
                    actions.push(UserActions.updateCurrentUserLocally({ user: savedUser }));
                  }
                  return actions;
                }
              }),
            );
          } else {
            // @todo do something else because we are offline.
            console.error('We are offline');
          }
        }
        return [SignUpStateActions.saveStateFailed({ error: 'User data is missing.' })];
      }),
    ),
  { functional: true },
);

export const saveClientProfileStep$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    userService = inject(UserService),
    growService = inject(GrowService),
  ) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveClientProfileStep),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[action, status], currentUser]) => {
        const user = action.user;
        if (status.connected) {
          if (user && user.email) {
            return from(growService.register(user)).pipe(
              mergeMap((savedUser) =>
                userService.postLead(savedUser, false).pipe(
                  mergeMap(() => {
                    const actions: Action[] = [];
                    if (action.newsletter) {
                      actions.push(SignUpStateActions.registerForNewsletter({ user: savedUser }));
                    }
                    return [
                      SignUpStateActions.saveClientProfileStepSuccess({ user: savedUser }),
                      SignUpStateActions.startNewListingAndFlow({ user: savedUser, currentUser }),
                      ...actions,
                    ];
                  }),
                  catchError((err) => [SignUpStateActions.saveClientProfileStepFailed({ error: err })]),
                ),
              ),
              catchError((err) => [SignUpStateActions.saveClientProfileStepFailed({ error: err })]),
            );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'User data is missing.' })];
        }
        return [NGRX_NO_ACTION];
        // Maybe nothing different if we are offline?
      }),
      catchError((err) => [SignUpStateActions.saveStateFailed({ error: err })]),
    ),
  { functional: true },
);

export const saveClientProfileStepFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveClientProfileStepFailed),
      mergeMap((action: any) => {
        const error = action.error;
        return [
          AlertActions.displayAlert({
            title: 'An Error Saving the Client Profile',
            body: 'An error occurred while attempting to save the client profile: ' + error,
            level: 'error',
          }),
        ];
      }),
    ),
  { functional: true },
);

export const registerForNewsletter$ = createEffect(
  (actions$ = inject(Actions), userMarketingService = inject(UserMarketingService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.registerForNewsletter),
      mergeMap((action) => {
        return userMarketingService.registerForNewsletter(action.user).pipe(mergeMap(() => [NGRX_NO_ACTION]));
      }),
    ),
  { functional: true },
);

export const uploadUserImage$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    userService = inject(UserService),
    spinnerService = inject(GlobalSpinnerService),
  ) =>
    actions$.pipe(
      ofType(SignUpStateActions.uploadUserProfileImage),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      switchMap(([[[action, authUser], user], status]) => {
        const files = action.formData;
        if (!files) {
          return of(NGRX_NO_ACTION);
        }

        if (!user || !user.id) {
          return [
            UserActions.userImageUploadError({
              error: 'User missing id',
            }),
          ];
        }

        spinnerService.spinnerOn();

        let id = user.id;

        if (user && user.id && action?.data?.id && user.id === action.data.id) {
          if (AuthHelpers.isAdminRole(authUser) || AuthHelpers.hasSalesOwnership(authUser, user)) {
            id = action.data.id;
          }
        }

        if (status.connected) {
          return userService.uploadImage(id, files).pipe(
            switchMap((updatedUser: IUser) => {
              spinnerService.spinnerOff();

              return [
                UserActions.userImageUploaded({
                  ...updatedUser,
                }),
              ];
            }),
            catchError((error) => {
              spinnerService.spinnerOff();

              return [
                UserActions.userImageUploadError({
                  error,
                }),
              ];
            }),
          );
        } else {
          setTimeout(() => spinnerService.spinnerOff());
          return [NetworkActions.deferUserImageUpload({ formData: files, data: { id } })];
        }
      }),
      catchError((error) => {
        spinnerService.spinnerOff();

        return [UserActions.userImageUploadError({ error })];
      }),
    ),
  { functional: true },
);

export const saveStateFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveStateFailed),
      mergeMap((action) => {
        return [
          SignUpStateActions.hideSpinner(),
          AlertActions.displayAlert({
            title: 'Error Saving Your Site or User',
            body: 'If you continue to encounter this error, please contact support.',
            level: 'error',
          }),
          LogActions.logMessage({
            log: {
              level: LogLevel.ERROR,
              message: 'Error Saving Your Site or User',
              details: action.error,
            },
          }),
        ];
      }),
    ),
  { functional: true },
);

export const saveStateSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SignUpStateActions.saveStateSuccess),
      mergeMap((action) => {
        if (action.listing && action.listing.id) {
          return [ListingActions.listingLoaded({ listing: action.listing })];
        } else if (action.user && action.user.id) {
          return [UserActions.userLoaded({ user: action.user })];
        } else {
          return [NGRX_NO_ACTION];
        }
      }),
    ),
  { functional: true },
);

export const restoreListing$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.restoreListing),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([[[[action, stateListing], stateUser], currentUser], status]) => {
        if (status.connected) {
          if (!stateListing?.id || (action.listingId && stateListing.id !== action.listingId)) {
            const listingId = stateUser?.inProgressId ?? currentUser.inProgressId;
            if (listingId) {
              return listingService.fetch(listingId).pipe(
                map((listing) => SignUpStateActions.restoreListingSuccess({ listing })),
                catchError((err) => [SignUpStateActions.restoreListingFailed({ error: err })]),
              );
            }
          } else if (action.listingId && stateListing.id === action.listingId) {
            // The site already exists in the state, just use it.
            return [SignUpStateActions.restoreListingSuccess({ listing: stateListing })];
          }
        }
        return [NGRX_NO_ACTION];
      }),
    ),
  { functional: true },
);

export const restoreListingSuccess$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), router = inject(Router)) =>
    actions$.pipe(
      ofType(SignUpStateActions.restoreListingSuccess),
      withLatestFrom(store.select(SignUpStateSelectors.getCurrentStep)),
      mergeMap(([action, currentStep]) => {
        if (currentStep) {
          return from(router.navigate(currentStep.route)).pipe(map(() => NGRX_NO_ACTION));
        } else {
          return [NGRX_NO_ACTION];
        }
      }),
    ),
  { functional: true },
);

export const restoreUser$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), authService = inject(AuthService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.restoreUser),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[action, stateUser], currentUser]) => {
        if (!stateUser?.id) {
          if (currentUser.id) {
            return [SignUpStateActions.restoreUserSuccess({ user: currentUser })];
          } else {
            console.warn("We hit the spot that we shouldn't have hit!");
            // Hopefully we won't need to do this, really ever.
            return authService.getCurrentUser().pipe(
              map((user) => SignUpStateActions.restoreUserSuccess({ user })),
              catchError((err) => [SignUpStateActions.restoreUserFailed({ error: err })]),
            );
          }
        }
        return [NGRX_NO_ACTION];
      }),
    ),
  { functional: true },
);

export const restoreUserSuccess$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), router = inject(Router)) =>
    actions$.pipe(
      ofType(SignUpStateActions.restoreUserSuccess),
      withLatestFrom(store.select(SignUpStateSelectors.getUser)),
      withLatestFrom(store.select(SignUpStateSelectors.getCurrentStep)),
      mergeMap(([[action, user], currentStep]) => {
        if (
          user?.inProgressId &&
          user?.role !== Role.guest &&
          (user?.role?.toLowerCase() as Role) !== Role.salesManager &&
          user?.role !== Role.sales
        ) {
          return [SignUpStateActions.restoreListing({ listingId: user.inProgressId })];
        } else {
          if (currentStep) {
            return from(router.navigate(currentStep.route)).pipe(map(() => NGRX_NO_ACTION));
          } else {
            return [NGRX_NO_ACTION];
          }
        }
      }),
    ),
  { functional: true },
);

// This effect hides the spinner after 30 seconds if the spinner is still active
export const displaySpinner$ = createEffect(
  (actions$ = inject(Actions), platformId = inject(PLATFORM_ID)) =>
    actions$.pipe(
      ofType(SignUpStateActions.displaySpinner),
      debounceTime(isPlatformServer(platformId) ? 0 : SPINNER_DEBOUNCE_TIME),
      map(() => SignUpStateActions.hideSpinner()),
    ),
  { functional: true },
);

export const publishListing$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(SignUpStateActions.publishListing),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      mergeMap(([action, listing]) => {
        if (listing && Object.keys(listing)?.length) {
          const actions: Action[] = [SignUpStateActions.displaySpinner(), ListingActions.enableListing({ listing })];

          if (listing.sites?.length === 1) {
            const site: ISite = listing.sites[0];
            actions.push(SiteActions.enableSite({ site: { ...site } }));
          }

          return actions;
        } else {
          return [
            //SignUpStateActions.displaySpinner(),
            NGRX_NO_ACTION,
          ];
        }
      }),
      catchError((err) => {
        return [
          AlertActions.displayAlert({
            title: 'Failed to Publish Site',
            body: 'An error occurred and we were unable to publish your site.',
            level: 'error',
          }),
          LogActions.logMessage({
            log: {
              level: LogLevel.ERROR,
              message: 'Publish Site Failure: ' + err.message,
              details: JSON.stringify(err),
            },
          }),
          SignUpStateActions.publishListingFailed(err),
        ];
      }),
    ),
  { functional: true },
);

export const publishBoondock$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(SignUpStateActions.publishBoondock),
      withLatestFrom(store.select(SignUpStateSelectors.getBoondock)),
      mergeMap(([action, boondock]) => {
        if (boondock && Object.keys(boondock)?.length) {
          return [SignUpStateActions.displaySpinner(), BoondockActions.enableBoondock({ boondock })];
        } else {
          return [NGRX_NO_ACTION];
        }
      }),
      catchError((err) => {
        return [
          AlertActions.displayAlert({
            title: 'Failed to Publish Boondock',
            body: 'An error occurred and we were unable to publish your boondock.',
            level: 'error',
          }),
          LogActions.logMessage({
            log: {
              level: LogLevel.ERROR,
              message: 'Publish Boondock Failure: ' + err.message,
              details: JSON.stringify(err),
            },
          }),
          SignUpStateActions.publishBoondockFailed(err),
        ];
      }),
    ),
  { functional: true },
);

export const publishLater$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), navCtrl = inject(NavController)) =>
    actions$.pipe(
      ofType(SignUpStateActions.publishLater),
      withLatestFrom(store.select(SignUpStateSelectors.getListing)),
      mergeMap(([action, listing]) => {
        if (listing && Object.keys(listing)?.length) {
          return of(navCtrl.navigateForward('sign-up/not-published')).pipe(
            exhaustMap(() => [SignUpStateActions.clearInProgressListing()]),
          );
        } else {
          return [NGRX_NO_ACTION];
        }
      }),
      catchError((err) => {
        return [
          AlertActions.displayAlert({
            title: 'Failed to Update Listing',
            body: 'An error occurred and we were unable to update your listing status.',
            level: 'error',
          }),
          LogActions.logMessage({
            log: {
              level: LogLevel.ERROR,
              message: 'Publish Later Failure: ' + err.message,
              details: JSON.stringify(err),
            },
          }),
          SignUpStateActions.publishLaterFailed(err),
        ];
      }),
    ),
  { functional: true },
);

export const boondockPublishLater$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), navCtrl = inject(NavController)) =>
    actions$.pipe(
      ofType(SignUpStateActions.boondockPublishLater),
      withLatestFrom(store.select(SignUpStateSelectors.getBoondock)),
      mergeMap(([action, boondock]) => {
        if (boondock && Object.keys(boondock)?.length) {
          navCtrl.navigateForward('sign-up/not-published');
        }

        return [NGRX_NO_ACTION];
      }),
      catchError((err) => {
        return [
          AlertActions.displayAlert({
            title: 'Failed to Update Boondock',
            body: 'An error occurred and we were unable to update your boondock status.',
            level: 'error',
          }),
          LogActions.logMessage({
            log: {
              level: LogLevel.ERROR,
              message: 'Publish Later Failure: ' + err.message,
              details: JSON.stringify(err),
            },
          }),
          SignUpStateActions.boondockPublishLaterFailed(err),
        ];
      }),
    ),
  { functional: true },
);

export const clearInProgressListing$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(SignUpStateActions.clearInProgressListing),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[[action, user], status], authUser]) => {
        if (status.connected) {
          if (user) {
            const updatedUser = { ...user, inProgressId: undefined };
            return userService.update(updatedUser).pipe(
              mergeMap(() => {
                const actions: Action[] = [SignUpStateActions.clearInProgressListingSuccess()];
                if (AuthHelpers.isUserRole(authUser)) {
                  actions.push(UserActions.updateCurrentUserLocally({ user: updatedUser }));
                }
                return actions;
              }),
            );
          }
          return [SignUpStateActions.saveStateFailed({ error: 'User data is missing.' })];
        } else {
          const actions: Action[] = [SignUpStateActions.clearInProgressListingSuccess()];
          if (user) {
            actions.push(NetworkActions.deferUserUpdate({ user }));
          }
          return actions;
        }
      }),
    ),
  { functional: true },
);

export const startNewListingAndFlow$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SignUpStateActions.startNewListingAndFlow),
      mergeMap((action) => {
        return [SignUpStateActions.startNewListing({ user: action.user })];
      }),
    ),
  { functional: true },
);

export const startNewListing$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    userService = inject(UserService),
    listingService = inject(ListingService),
  ) =>
    actions$.pipe(
      ofType(SignUpStateActions.startNewListing),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[action, status], currentUser]) => {
        const listing = new Listing();
        if (listing && (action.user || currentUser)) {
          let user: IUser;
          if (action.user && currentUser !== action.user) {
            user = action.user;
            listing.user = new User({ id: user.id });
          } else {
            user = currentUser;
            listing.user = new User({ id: currentUser.id });
          }
          if (status.connected) {
            return listingService.create(listing).pipe(
              mergeMap((listingResult) => {
                const userToSave = { ...user, inProgressId: listingResult.id };
                return userService.update(userToSave).pipe(
                  mergeMap((userResult) => {
                    const actions: Action[] = [
                      SignUpStateActions.startNewListingSuccess({ listing: listingResult, user: userResult }),
                    ];
                    if (AuthHelpers.isUserRole(currentUser)) {
                      actions.push(UserActions.updateCurrentUserLocally({ user: userResult }));
                    }
                    return actions;
                  }),
                  catchError((err) => {
                    return [
                      LogActions.logMessage({
                        log: {
                          level: LogLevel.ERROR,
                          message: 'Failed to start new listing.',
                          details: err,
                        },
                      }),
                      SignUpStateActions.startNewListingFailed({ error: err }),
                    ];
                  }),
                );
              }),
              catchError((err) => {
                return [
                  LogActions.logMessage({
                    log: {
                      level: LogLevel.ERROR,
                      message: 'Failed to start new listing.',
                      details: err,
                    },
                  }),
                  SignUpStateActions.startNewListingFailed({ error: err }),
                ];
              }),
            );
          } else {
            return [SignUpStateActions.startNewListingSuccess({ listing, user })];
          }
        } else {
          return [NGRX_NO_ACTION];
        }
      }),
      catchError((err) => {
        return [
          LogActions.logMessage({
            log: {
              level: LogLevel.ERROR,
              message: 'Failed to start new listing.',
              details: err,
            },
          }),
          SignUpStateActions.startNewListingFailed({ error: err }),
        ];
      }),
    ),
  { functional: true },
);

export const startNewListingSuccess$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), router = inject(Router)) =>
    actions$.pipe(
      ofType(SignUpStateActions.startNewListingSuccess),
      withLatestFrom(store.select(SignUpStateSelectors.getCurrentStep)),
      mergeMap(([action, currentStep]) => {
        let route = ['', 'sign-up'];
        if (currentStep) {
          route = currentStep.route;
        }
        return from(router.navigate(route)).pipe(map(() => NGRX_NO_ACTION));
      }),
    ),
  { functional: true },
);

export const startNewListingFailed$ = createEffect(
  (actions$ = inject(Actions), alertController = inject(AlertController), router = inject(Router)) =>
    actions$.pipe(
      ofType(SignUpStateActions.startNewListingFailed),
      mergeMap(async (action) => {
        const alert = await alertController.create({
          header: 'Error Creating The Listing',
          message: `Something is preventing your listing from being created. Check your internet connection and click the 'Add Property' from the 'Your Listings'. Error: ${action.error}`,
          backdropDismiss: false,
          buttons: [
            {
              text: 'Your Listings',
              handler: () => {
                router.navigate(['', 'listings']);
              },
            },
          ],
        });

        await alert.present();
        return NGRX_NO_ACTION;
      }),
    ),
  { functional: true },
);

export const startNewBoondock$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    userService = inject(UserService),
    boondockService = inject(BoondockService),
  ) =>
    actions$.pipe(
      ofType(SignUpStateActions.startNewBoondock),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      mergeMap(([[action, status], currentUser]) => {
        const boondock = new Boondock();
        if (boondock && (action.user || currentUser)) {
          let user: IUser;
          if (currentUser !== action.user && action.user) {
            user = action.user;
          } else {
            user = currentUser;
          }
          if (status.connected) {
            return boondockService.create(boondock).pipe(
              mergeMap((boondockResult) => {
                const userToSave = { ...user };
                return userService.update(userToSave).pipe(
                  mergeMap((userResult) => {
                    const actions: Action[] = [
                      SignUpStateActions.startNewBoondockSuccess({ boondock: boondockResult, user: userResult }),
                    ];
                    return actions;
                  }),
                );
              }),
            );
          } else {
            return [SignUpStateActions.startNewBoondockSuccess({ boondock, user })];
          }
        } else {
          return [NGRX_NO_ACTION];
        }
      }),
      catchError((err) => {
        return [
          LogActions.logMessage({
            log: {
              level: LogLevel.ERROR,
              message: 'Failed to start new boondock.',
              details: err,
            },
          }),
          SignUpStateActions.startNewBoondockFailed({ error: err }),
        ];
      }),
    ),
  { functional: true },
);

export const startNewBoondockSuccess$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), router = inject(Router)) =>
    actions$.pipe(
      ofType(SignUpStateActions.startNewBoondockSuccess),
      withLatestFrom(store.select(SignUpStateSelectors.getCurrentStep)),
      mergeMap(([action, currentStep]) => {
        let route = ['', 'sign-up'];
        if (currentStep) {
          route = currentStep.route;
        }
        return from(router.navigate(route)).pipe(map(() => NGRX_NO_ACTION));
      }),
    ),
  { functional: true },
);

export const setInProgressListing$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(SignUpStateActions.setInProgressListing),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      map(([action, user]) => UserActions.setInProgressListing({ listing: action.listing, user })),
    ),
  { functional: true },
);

export const sendOwnerEmailSuccess$ = createEffect(
  (actions$ = inject(Actions), alertController = inject(AlertController), router = inject(Router)) =>
    actions$.pipe(
      ofType(SignUpStateActions.sendOwnerEmailSuccess),
      mergeMap(async (action) => {
        const alert = await alertController.create({
          header: 'Client Welcome Email Has Been Sent',
          message: `The client welcome email has been sent to ${action.user.email}. You can now stop updating the listing and wait for the client to publish it. Or you can continue to update the listing.`,
          backdropDismiss: true,
          buttons: [
            {
              text: 'Your Clients',
              handler: () => {
                router.navigate(['', 'user-dashboard', 'dashboard', { section: 'grow-clients' }]);
              },
            },
            {
              text: 'Close',
              role: 'cancel',
            },
          ],
        });

        await alert.present();
        return NGRX_NO_ACTION;
      }),
    ),
  { functional: true },
);

export const sendOwnerEmailFailed$ = createEffect(
  (actions$ = inject(Actions), alertController = inject(AlertController), router = inject(Router)) =>
    actions$.pipe(
      ofType(SignUpStateActions.sendOwnerEmailFailed),
      mergeMap(async (action) => {
        const alert = await alertController.create({
          header: 'Error Sending Client Welcome Email',
          message: `Unable to send the client welcome email. The reason is: ${JSON.stringify(action.err)}`,
          backdropDismiss: true,
          buttons: [
            {
              text: 'Your Clients',
              handler: () => {
                router.navigate(['', 'user-dashboard', 'dashboard', { section: 'grow-clients' }]);
              },
            },
            {
              text: 'Close',
              role: 'cancel',
            },
          ],
        });

        await alert.present();
        return NGRX_NO_ACTION;
      }),
    ),
  { functional: true },
);
